import { useNavigate } from 'react-router-dom'

import { mixpanel } from '@/mixpanel'
import { openUrl } from '@/utils/external-resources'
import { useRelatedDocuments, useRelatedMessages, useRelatedTasks } from './useRelated'

import type { DocumentMessageActivity } from '@/modules/documents/types/documents.types'
import type { Message } from '@/modules/messages/types/message.types'
import type { Task } from '@/modules/task/types/task.types'

/**
 * The tasks, messages and documents that share a trigger with an agenda item, and what opening
 * each of them does. A page hands the result to RelatedItems, which stays a view.
 */
function useRelatedItems(trigger?: string) {
  const navigate = useNavigate()
  const tasks = useRelatedTasks(trigger)
  const messages = useRelatedMessages(trigger)
  const documents = useRelatedDocuments(trigger)

  return {
    isLoading: tasks.isLoading || messages.isLoading || documents.isLoading,
    tasks: tasks.data?.data ?? [],
    messages: messages.data?.data ?? [],
    documents: documents.data ?? [],
    onOpenTask: (task: Task) => {
      mixpanel.track('timeline_related_task_open')
      navigate(`/task/${task.id}`, { state: { from: 'timeline' } })
    },
    onOpenMessage: (message: Message) => {
      mixpanel.track('timeline_related_message_open')
      navigate(`/message/${message.id}`, { state: { from: 'timeline' } })
    },
    // A document with a file opens the file; one without opens its own page.
    onOpenDocument: (document: DocumentMessageActivity) => {
      mixpanel.track(`timeline_related_${document.meta?.messageType?.toLowerCase() ?? 'document'}_open`)

      const url = document.meta?.attachments?.[0]?.url
      if (url) {
        openUrl(url)
      } else {
        navigate(`/documents/personal/${document.id}`, { state: { from: 'timeline' } })
      }
    },
  }
}

export { useRelatedItems }
